import React, { useContext, useState } from 'react'
import type { Review as ReviewType } from '../supplier_show/prop_shapes/review'
import { DirectMessageModal } from 'components/inbox/message_modals/DirectMessageModal'
import { PrivateMessageModal } from 'components/inbox/message_modals/PrivateMessageModal'
import { PrivateMessageChooserModal } from 'components/inbox/message_modals/PrivateMessageChooserModal'
import { PrivateMessageThreadModal } from 'components/inbox/message_modals/PrivateMessageThreadModal'
import { fetchInboxConversations } from 'components/inbox/services/fetchInboxConversations'
import type { InboxConversationSummary, ReviewerDisplayState } from 'components/inbox/inboxTypes'
import { FeatureContext } from 'components/FeatureContext'

interface AuthorInfoProps {
  review: ReviewType
  renderMode?: 'default' | 'messageReviewerModal'
}

export function AuthorInfo ({ review, renderMode = 'default' }: AuthorInfoProps): JSX.Element {
  const { author } = review
  const [modalType, setModalType] = useState<'closed' | 'directMessage' | 'privateChooser' | 'privateCompose' | 'privateExisting'>('closed')
  const [existingConversations, setExistingConversations] = useState<InboxConversationSummary[]>([])
  const [activePrivateConversationId, setActivePrivateConversationId] = useState<number | null>(null)
  const featureFlags = useContext(FeatureContext)
  const inboxEnabled = featureFlags?.inboxEnabled ?? false
  const privateMessagesEnabled = featureFlags?.privateMessagesEnabled ?? false
  const canStartConversation = Boolean(review.can_start_conversation)
  const showMessageButton = canStartConversation &&
    renderMode !== 'messageReviewerModal'
  const usePrivateMessageModal = privateMessagesEnabled && review.share_with_supplier
  const messageButtonLabel = usePrivateMessageModal ? 'Private Message' : 'Direct Message'

  const openDirectMessageModal = (): void => setModalType('directMessage')
  const openPrivateComposeModal = (): void => setModalType('privateCompose')
  const openPrivateExistingModal = (conversationId?: number): void => {
    if (conversationId != null) setActivePrivateConversationId(conversationId)
    setModalType('privateExisting')
  }
  const closeModal = (): void => {
    setModalType('closed')
    setExistingConversations([])
    setActivePrivateConversationId(null)
  }

  const handleMessageClick = async (): Promise<void> => {
    if (!inboxEnabled) return

    if (!usePrivateMessageModal) {
      openDirectMessageModal()
      return
    }

    try {
      const conversations = await fetchInboxConversations(review.id)

      if (conversations.length === 0) {
        openPrivateComposeModal()
        return
      }

      setExistingConversations(conversations)
      setModalType('privateChooser')
    } catch {
      openPrivateComposeModal()
    }
  }

  const reviewerDisplayState: ReviewerDisplayState =
    review.is_anonymous === true ? 'RECIPIENT_ANONYMOUS' : 'ALL_VISIBLE'

  return (
    <>
      <div className='grid-x'>
        <div className='shrink cell'>
          <img
            src={author.thumbnail}
            alt={author.name}
            className='img-rounded img-responsive'
            width='40'
            height='40'
          />
        </div>

        <div className='auto cell'>
          <div className='author-details'>
            <div className='font-bold pro-text-xs'>{author.name}</div>
            <div className='font-italic'>{author.organization}</div>

            {showMessageButton && (
              <a
                href='#'
                className='message-reviewer-link pro-text-xs bold'
                data-testid={`message-reviewer-link-${review.id}`}
                onClick={(e) => {
                  e.preventDefault()
                  void handleMessageClick()
                }}
              >
                <i className='fa-regular fa-comment' /> {messageButtonLabel}
              </a>
            )}
          </div>
        </div>
      </div>

      {modalType === 'privateCompose' && (
        <PrivateMessageModal
          review={review}
          reviewerDisplayState={reviewerDisplayState}
          onClose={closeModal}
          onConversationCreated={openPrivateExistingModal}
        />
      )}

      {modalType === 'privateChooser' && existingConversations.length > 0 && (
        <PrivateMessageChooserModal
          review={review}
          conversations={existingConversations}
          onClose={closeModal}
          onStartNewConversation={openPrivateComposeModal}
          onOpenExistingConversation={(conversationId) => {
            openPrivateExistingModal(conversationId)
          }}
        />
      )}

      {modalType === 'privateExisting' && (activePrivateConversationId != null || existingConversations[0] != null) && (
        <PrivateMessageThreadModal
          review={review}
          reviewerDisplayState={reviewerDisplayState}
          onClose={closeModal}
          fetchConversation={async () => {
            const conversationId = activePrivateConversationId ?? existingConversations[0]?.id

            if (conversationId == null) {
              throw new Error('No conversation ID available')
            }

            return { id: conversationId }
          }}
        />
      )}

      {modalType === 'directMessage' && (
        <DirectMessageModal
          review={review}
          reviewerDisplayState={reviewerDisplayState}
          onClose={closeModal}
        />
      )}
    </>
  )
}
